import React, { useCallback, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { useCss } from 'react-use';
import cn from 'classnames';
import moment from 'moment';
import { bindActionCreators } from 'redux';

import calculateBlockHeight from 'bl/scheduler/calculateBlockHeight.bl';
import determineIfDisabledDrag from 'bl/scheduler/determineIfDisabledDrag.bl';
import { determineIfTimeOffMoveBlocked } from 'bl/scheduler/determineIfTimeOffMoveBlocked.bl';
import determineOpeningInterval from 'bl/scheduler/determineOpeningInterval.bl';
import DndDraggable from 'components/dnd/DndDraggable.component';
import Tooltip from 'components/generic/Tooltip.component';
import BOTTOM_GRD_COLOR_NAME from 'constants/bottomGradientColors.const';
import COLOR_NAME from 'constants/colors.const';
import TOP_GRD_COLOR_NAME from 'constants/topGradientColors.const';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import { useTimeOffPermissions } from 'hooks/useTimeOffPermissions.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { selectCurrentSalonTimeZone } from 'selectors/currentSalon.selectors';
import { selectSidebarPanel, selectSidebarProcedureModalMounted } from 'selectors/sidebar.selectors';
import { selectTimeOffData, selectTimeOffNonce } from 'selectors/timeoff.selectors';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import { fromAnyDateToIso } from 'utils/converters/time/from.util';
import { combineDateTimeZoneZulu } from 'utils/date/combine.util';
import { formatDayOfWeek, formatHourAndMinuteShort, formatPartTime } from 'utils/date/format.util';
import { timeAdd15min } from 'utils/date/manipulate.util';
import zonedMoment from 'utils/date/zonedMoment.util';
import elementScrollTo from 'utils/dom/elementScrollTo.util';
import isTouchDevice from 'utils/dom/isTouchDevice.util';
import withOptions from 'utils/functions/withOptions.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import parseJson from 'utils/parsers/parseJson';
import { isTimeAfterOrEqual, isTimeBeforeOrEqual } from 'utils/predicates/time/compare.util';
import dnd from 'utils/react/dnd.util';

import { setCreation, setModal } from 'scheduler/actions';
import EventModal from 'scheduler/components/EventModal';

import CN from './ScheduleEvent.module.scss';

const L = logger('ScheduleEvent');

const USE_MUTEX = false;
const USE_SCROLL = true;
const USE_DEBUG_LOG = false;

const DRAG_HANDLER = 6;
const DEFAULT_BACKGROUND_COLOR = '#bebebe';
const END_STYLE = Object.freeze({ paddingLeft: 0 });

const LIGHT_BACKGROUNDS = Object.freeze([COLOR_NAME.greenLight, COLOR_NAME.purpleLight, COLOR_NAME.pinkLight, COLOR_NAME.grayLight, COLOR_NAME.blueLight, COLOR_NAME.yellow, COLOR_NAME.orange]);

// Category predefined colors
const TOP_GRADIENT_COLORS = Object.freeze({
  [COLOR_NAME.grayLight]: TOP_GRD_COLOR_NAME.grayLight,
  [COLOR_NAME.grayDark]: TOP_GRD_COLOR_NAME.grayDark,
  [COLOR_NAME.black]: TOP_GRD_COLOR_NAME.black,
  [COLOR_NAME.purpleLight]: TOP_GRD_COLOR_NAME.purpleLight,
  [COLOR_NAME.purple]: TOP_GRD_COLOR_NAME.purple,
  [COLOR_NAME.purpleDark]: TOP_GRD_COLOR_NAME.purpleDark,
  [COLOR_NAME.greenLight]: TOP_GRD_COLOR_NAME.greenLight,
  [COLOR_NAME.green]: TOP_GRD_COLOR_NAME.green,
  [COLOR_NAME.blueLight]: TOP_GRD_COLOR_NAME.blueLight,
  [COLOR_NAME.blue]: TOP_GRD_COLOR_NAME.blue,
  [COLOR_NAME.blueMatte]: TOP_GRD_COLOR_NAME.blueMatte,
  [COLOR_NAME.blueNavy]: TOP_GRD_COLOR_NAME.blueNavy,
  [COLOR_NAME.yellow]: TOP_GRD_COLOR_NAME.yellow,
  [COLOR_NAME.orange]: TOP_GRD_COLOR_NAME.orange,
  [COLOR_NAME.pinkLight]: TOP_GRD_COLOR_NAME.pinkLight,
  [COLOR_NAME.red]: TOP_GRD_COLOR_NAME.red,
  [COLOR_NAME.redDeep]: TOP_GRD_COLOR_NAME.redDeep,
  [COLOR_NAME.redDark]: TOP_GRD_COLOR_NAME.redDark,
});

const BOTTOM_GRADIENT_COLORS = Object.freeze({
  [COLOR_NAME.grayLight]: BOTTOM_GRD_COLOR_NAME.grayLight,
  [COLOR_NAME.grayDark]: BOTTOM_GRD_COLOR_NAME.grayDark,
  [COLOR_NAME.black]: BOTTOM_GRD_COLOR_NAME.black,
  [COLOR_NAME.purpleLight]: BOTTOM_GRD_COLOR_NAME.purpleLight,
  [COLOR_NAME.purple]: BOTTOM_GRD_COLOR_NAME.purple,
  [COLOR_NAME.purpleDark]: BOTTOM_GRD_COLOR_NAME.purpleDark,
  [COLOR_NAME.greenLight]: BOTTOM_GRD_COLOR_NAME.greenLight,
  [COLOR_NAME.green]: BOTTOM_GRD_COLOR_NAME.green,
  [COLOR_NAME.blueLight]: BOTTOM_GRD_COLOR_NAME.blueLight,
  [COLOR_NAME.blue]: BOTTOM_GRD_COLOR_NAME.blue,
  [COLOR_NAME.blueMatte]: BOTTOM_GRD_COLOR_NAME.blueMatte,
  [COLOR_NAME.blueNavy]: BOTTOM_GRD_COLOR_NAME.blueNavy,
  [COLOR_NAME.yellow]: BOTTOM_GRD_COLOR_NAME.yellow,
  [COLOR_NAME.orange]: BOTTOM_GRD_COLOR_NAME.orange,
  [COLOR_NAME.pinkLight]: BOTTOM_GRD_COLOR_NAME.pinkLight,
  [COLOR_NAME.red]: BOTTOM_GRD_COLOR_NAME.red,
  [COLOR_NAME.redDeep]: BOTTOM_GRD_COLOR_NAME.redDeep,
  [COLOR_NAME.redDark]: BOTTOM_GRD_COLOR_NAME.redDark,
});

// Default category Holiday and  Meeting with Slick

const TOP_DEFAULT_GRD_COLORS = {
  [COLOR_NAME.defaultGreen]: TOP_GRD_COLOR_NAME.defaultGreen,
  [COLOR_NAME.defaultPurple]: TOP_GRD_COLOR_NAME.defaultPurple,
};

const BOTTOM_DEFAULT_GRD_COLORS = {
  [COLOR_NAME.defaultGreen]: BOTTOM_GRD_COLOR_NAME.defaultGreen,
  [COLOR_NAME.defaultPurple]: BOTTOM_GRD_COLOR_NAME.defaultPurple,
};

const byTransform = (e) => fromAnyToBool(e.parentElement.style.transform);

const determineEndAsIso = ({ isPatchTest, bgn, end, tz, last }) => {
  if (isPatchTest) return timeAdd15min(bgn);

  const rezoned = tz ? zonedMoment(tz, end) : moment(end);
  if (isTimeAfterOrEqual(rezoned, last)) return timeAdd15min(last);

  return fromAnyDateToIso(end);
};

const STRAT_OCH = 'open-close-hours';
const STRAT_EIGHT = 'simple-eight';

const determineIfAllDay = withOptions(({ bgn, end, hours, tz, strat }) => {
  if (strat === STRAT_EIGHT) {
    // NOTE: maybe even when !eventBgn || !eventEnd || !openingHours
    return Math.abs(new Date(end) - new Date(bgn)) / 3600000 >= 8;
  }

  if (!bgn || !end || !hours) return false;
  const { open, close } = determineOpeningInterval({ hours, bgn, end });

  if (!isTimeBeforeOrEqual(bgn, combineDateTimeZoneZulu(bgn, open, tz))) return false;
  // noinspection RedundantIfStatementJS
  if (!isTimeBeforeOrEqual(combineDateTimeZoneZulu(bgn, close, tz), moment(end))) return false;

  return true;
});

const ScheduleEvent = ({
  allowModal,
  classKey,
  dayView,
  event,
  firstTime,
  isBeingDragged,
  isProcedureModalMounted,
  lastTime,
  nonce: nonceFromProps,
  onDeleteEvent,
  onEventModalLoad,
  onEventModalUnload,
  openingHours: hours,
  tag,
  timeOffNonce: nonceFromStore,
  toffData,
  tz,
}) => {
  const {
    id: eventId,
    client_name: clientName,
    end_time: endEvent,
    notes,
    panel,
    recurrences,
    status,
    start_time: bgnEvent,
    stylist_id: eventStylistId,
    time_off_category: timeOffCategory,
  } = asObject(event);

  const { t } = useTranslation();
  const { startTime: bgnToff, endTime: endToff } = asObject(toffData);

  const isEdit = fromAnyToBool(nonceFromStore && nonceFromStore === nonceFromProps);
  const bgnIso = fromAnyDateToIso(zonedMoment(tz, isEdit ? bgnToff || bgnEvent : bgnEvent));
  const endIso = fromAnyDateToIso(zonedMoment(tz, isEdit ? endToff || endEvent : endEvent));

  const wrapperRef = useRef(null);
  const modalRef = useRef(null);

  const [originalH, setOriginalH] = useStateWhileMounted(0);
  const { val: isOpen, yay: openModal, nay: closeModal } = useTip(false);

  const { isCreateBlocked, isCreatePersonalOnly, isDeleteBlocked, isDeletePersonalOnly, isEditDurationBlocked, isEditDurationPersonalOnly, accessStylistId, permsActive } = useTimeOffPermissions();

  const timeOffPerms = { permsActive, isEditDurationBlocked, isEditDurationPersonalOnly, accessStylistId };
  const isEditDurDisabled = determineIfTimeOffMoveBlocked({ timeOffPerms, eventStylistId });
  const isFullyBlockedForColumn =
    permsActive &&
    (isCreateBlocked || (isCreatePersonalOnly && eventStylistId !== accessStylistId)) &&
    (isDeleteBlocked || (isDeletePersonalOnly && eventStylistId !== accessStylistId)) &&
    isEditDurDisabled;

  const color = isOpen ? '#FFFFFF' : '#3e0b6c';
  const isPatchTest = status === 'patch_test';

  const firstIso = fromAnyDateToIso(firstTime);
  const lastTimeIso = fromAnyDateToIso(lastTime);
  const lastIso = determineEndAsIso({ isPatchTest, bgn: bgnIso, end: endIso, tz, last: lastTimeIso });

  const height = calculateBlockHeight({ bgn: bgnIso, end: endIso, first: firstIso, last: lastIso, hours, tz });

  const isAllDayPerOpeningHours = determineIfAllDay({ bgn: bgnIso, end: lastIso, hours, tz, strat: STRAT_OCH });
  const isAllDayPerEightHours = determineIfAllDay({ bgn: bgnIso, end: endIso, strat: STRAT_EIGHT });

  const transformSize = withOptions(({ style, snapshot, height, handle, backgroundColor }) => {
    const eventId = asObject(event).id;
    const { transform } = asObject(style);
    const { isDragging, isDropAnimating } = asObject(snapshot);

    if (handle && transform && eventId) {
      const [theEventDragged] = [...document.querySelectorAll(`.event${eventId}`)].filter(byTransform);
      const heightChange = parseInt(transform.split(', ')[1], 10);
      const oldH = theEventDragged ? parseInt(theEventDragged.style.height, 10) : 0;

      const needsOriginalH = !originalH && isDragging && oldH > 0;
      if (needsOriginalH) setOriginalH(oldH);

      if (theEventDragged) {
        theEventDragged.style.height = `${
          // eslint-disable-next-line no-nested-ternary
          needsOriginalH ? oldH + heightChange : isDragging ? originalH + heightChange : 0
        }px`;
      }
    }

    const base = { ...style, top: `${height - DRAG_HANDLER}px`, width: '100%', borderBottomColor: backgroundColor };

    if (isDropAnimating) return { ...base, transitionDuration: '0.001s' }; // cannot be 0, so make it super tiny
    if (isDragging) return { ...base, visibility: 'hidden', backgroundColor: 'transparent' };

    return base;
  });

  const handleScrollRef = useCallback(
    (element) => {
      L.debug('handleScrollRef()', 'USE_SCROLL:', USE_SCROLL, 'element:', element);

      if (isAllDayPerEightHours) return;
      if (USE_SCROLL) elementScrollTo({ element, behavior: 'smooth', block: 'center', inline: 'center' });
    },
    [isAllDayPerEightHours],
  );

  const handleOpenModal = useCallback(() => {
    L.debug('handleOpenModal()', isOpen);
    if (isFullyBlockedForColumn) return;
    if (!isOpen) openModal();
  }, [isFullyBlockedForColumn, isOpen, openModal]);

  const blockStyle = useMemo(() => ({ height: `${height}px` }), [height]);
  const infoStyle = useMemo(() => ({ overflow: 'hidden', height: `${height}px` }), [height]);
  const entryWrapStyle = useMemo(() => ({ color, height: `${height}px`, width: '100%' }), [color, height]);

  const backgroundColor = asObject(timeOffCategory).color || DEFAULT_BACKGROUND_COLOR;

  const topColor = useCallback(() => TOP_GRADIENT_COLORS[backgroundColor], [backgroundColor]);
  const bottomColor = useCallback(() => BOTTOM_GRADIENT_COLORS[backgroundColor], [backgroundColor]);
  const defaultTopColor = useCallback(() => TOP_DEFAULT_GRD_COLORS[backgroundColor], [backgroundColor]);

  const defaultBottomColor = useCallback(() => BOTTOM_DEFAULT_GRD_COLORS[backgroundColor], [backgroundColor]);

  const isDefaultCat = [COLOR_NAME.defaultGreen, COLOR_NAME.defaultPurple].includes(backgroundColor);

  const componentStyle = useCss({
    color,
    height: `${height}px`,
    width: '100%',
    opacity: isFullyBlockedForColumn ? '0.5' : '1',
    border: isBeingDragged ? '3px solid red' : 'none',
    backgroundColor,
    backgroundImage: `linear-gradient(to bottom right, ${isDefaultCat ? defaultTopColor() : topColor()}, ${isDefaultCat ? defaultBottomColor() : bottomColor()})`,
  });

  const componentClasses = cn({
    'cal-container time-off-bgr': true,
    'cal-active': isOpen && allowModal,
    'cal-inactive': !(isOpen && allowModal),
    'if-all-day-time-off': isAllDayPerOpeningHours,
    'time-off-to': !isAllDayPerOpeningHours,
    patch: isPatchTest,
    [classKey]: classKey,
    [componentStyle]: componentStyle,
  });

  const recurrenceClasses = cn({
    'cal-name-recur cal-state-b': true,
    repeatIcon: recurrences,
  });

  // NOTE: we set FG color for dark and white BG card but there are also default time off category
  // that should be colored with white FG which comes from css as default
  const timeOffColorClasses = cn({
    'cal-client': true,
    [CN.darkFg]: LIGHT_BACKGROUNDS.includes(backgroundColor),
  });

  if (USE_DEBUG_LOG) L.debug('()', { eventId, allowModal, isOpen, height });

  const hasExplicitNoAccessBlock = isCreateBlocked || isDeleteBlocked || isEditDurationBlocked;
  const noAccessContent = isFullyBlockedForColumn ? t(hasExplicitNoAccessBlock ? 'scheduler.timeOffPermissions.noAccessTooltip' : 'scheduler.timeOffPermissions.personalOnlyTooltip') : null;

  return (
    <Tooltip content={noAccessContent} variant="top">
      <div
        data-bem="ScheduleEvent"
        data-testid={`ScheduleEvent__time-off-${eventId}`}
        role="button"
        data-event-id={eventId}
        data-height={height}
        data-bgn-iso={fromAnyDateToIso(bgnIso)}
        data-end-iso={fromAnyDateToIso(endIso)}
        tabIndex={0}
        className={componentClasses}
      >
        <div data-bem="ScheduleEvent__recurrence" className={recurrenceClasses}>
          <div data-bem="ScheduleEvent__entry" className="cal-entry card-time-off">
            <div
              data-bem="ScheduleEvent__entry-wrap"
              ref={wrapperRef}
              className="cal-entry-wrap disable-selection cal-state-bg-e"
              style={entryWrapStyle}
              onClick={handleOpenModal}
              role="button"
              tabIndex={0}
            >
              <div data-bem="ScheduleEvent__block" style={blockStyle}>
                <div data-bem="ScheduleEvent__info" className={timeOffColorClasses} style={infoStyle}>
                  {isAllDayPerEightHours ? (
                    <div className={CN.allDayTitle}>
                      {formatDayOfWeek(bgnIso)} - {t('scheduler.scheduleEvent.allDay')}
                    </div>
                  ) : (
                    <div data-bem="ScheduleEvent__calendar-time">
                      <div data-bem="ScheduleEvent__calendar-begin-time" className={CN.timeOffTime}>
                        {formatHourAndMinuteShort(bgnIso)}
                      </div>
                      <div data-bem="ScheduleEvent__calendar-end-time" className={CN.timeOffTime} style={END_STYLE}>
                        - {formatPartTime(endIso)}
                      </div>
                    </div>
                  )}

                  {status !== 'patch_test' && (
                    <div className={CN.timeOffTitle}>
                      {asObject(timeOffCategory).name}
                      <div className={CN.timeOffNotes}>{notes}</div>
                    </div>
                  )}
                  {fromAnyToBool(isPatchTest && clientName) && (
                    <div>
                      <span className="patch-label">{t('scheduler.scheduleEvent.patchTest')} </span>
                    </div>
                  )}
                </div>

                <div>
                  {fromAnyToBool(isOpen && allowModal) && (
                    <div data-bem="ScheduleEvent__modal-container" ref={modalRef}>
                      <div data-bem="ScheduleEvent__scroll-target" ref={handleScrollRef} />
                      <EventModal tag={tag} dayView={dayView} event={event} onClose={closeModal} onDeleteEvent={onDeleteEvent} onLoad={onEventModalLoad} onUnload={onEventModalUnload} />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {!isAllDayPerOpeningHours && (
              <DndDraggable
                tag="ScheduleEvent--dnd-source"
                draggableId={dnd.resizeEvent({ event })}
                index={event?.id || 0}
                isDragDisabled={determineIfDisabledDrag({ panel, modal: isProcedureModalMounted })}
              >
                {({ dragHandleProps, draggableProps, innerRef }, snapshot) => (
                  <div
                    {...draggableProps}
                    {...dragHandleProps}
                    ref={innerRef}
                    data-bem="ScheduleEvent--el-source"
                    data-testid={isEditDurDisabled ? `ScheduleEvent__resize-blocked-${eventId}` : `ScheduleEvent__resize-${eventId}`}
                    data-resize-blocked={isEditDurDisabled ? 'true' : undefined}
                    className={cn('cal-botline cal-state-bg-e', { 'cal-botline--move-blocked': isEditDurDisabled })}
                    style={transformSize({
                      style: draggableProps.style,
                      snapshot,
                      height,
                      handle: true,
                      backgroundColor,
                    })}
                  >
                    {isTouchDevice() && <span className="grabIndicator">=</span>}
                  </div>
                )}
              </DndDraggable>
            )}
          </div>
        </div>
      </div>
    </Tooltip>
  );
};

const Loader = ({ allowModal, event: eventFromProps, isModalAllowed: isModalAllowedFromProps, openingHours: openingHoursFromProps, setCreation, setModal, tag: tagFromProps, ...extra }) => {
  const tag = fromAnyToTrimmed(tagFromProps);

  // NOTE: using strings for deps in useMemo may reduce the need of complicated checks
  const jsonEvent = JSON.stringify(eventFromProps);
  const jsonHours = JSON.stringify(openingHoursFromProps);

  // NOTE: parseJson returns undefined in case of an error, so {...undefined} is still an object
  const event = useMemo(() => ({ ...parseJson(jsonEvent) }), [jsonEvent]);
  const hours = useMemo(() => ({ ...parseJson(jsonHours) }), [jsonHours]);

  const handleEventModalLoad = useCallback(() => {
    L.debug('handleEventModalLoad()', tag, setModal, setCreation);
    if (!USE_MUTEX) return;
    setModal(false);
    setCreation(false);
  }, [setCreation, setModal, tag]);

  const handleEventModalUnload = useCallback(() => {
    L.debug('handleEventModalUnload()', tag, setModal, setCreation);
    if (!USE_MUTEX) return;
    setModal(true);
    setCreation(true);
  }, [setCreation, setModal, tag]);

  if (USE_DEBUG_LOG) L.debug('Loader()', tag, event.id, allowModal, { event, hours, extra });

  return (
    <ScheduleEvent
      {...extra}
      tag={tag}
      allowModal={allowModal}
      event={event}
      onEventModalLoad={handleEventModalLoad}
      onEventModalUnload={handleEventModalUnload}
      openingHours={hours}
      setCreation={setCreation}
      setModal={setModal}
    />
  );
};

const mapStateToProps = (state) => ({
  isProcedureModalMounted: selectSidebarProcedureModalMounted(state),
  panel: selectSidebarPanel(state),
  timeOffNonce: selectTimeOffNonce(state),
  toffData: selectTimeOffData(state),
  tz: selectCurrentSalonTimeZone(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ setCreation, setModal }, dispatch);

const ScheduleEventHoc = connect(mapStateToProps, mapDispatchToProps)(Loader);

export default ScheduleEventHoc;
